import React, { useState, useEffect, useRef, useContext } from "react";
import cx from "classnames";
import styles from "../scss/AFPayment.module.scss";
import { useHistory, useParams } from "react-router-dom";
import { DirectDebitQuestionList } from "../constants/DirectDebitData";
import { Form, Formik } from "formik";
import AppFormField from "./AppFormField";
import { IAnswerProps, IDebitDetails, IPageInfo, IPaymentDetails } from "../interfaces/IAppFormData";
import { PageType } from "../enums/PageType";
import { getOptions } from "../shared/AppFormHelper";
import Dropdown from "../../common/components/Forms/Dropdown/Dropdown";
import Checkbox from "../../common/components/Forms/Checkbox/Checkbox";
import Input from "../../common/components/Forms/Input/Input";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import { addPayment, getSectionContent, setCurrentPageProgress } from "../api/ApiAppForm";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import { useAppFormUtils } from "../hooks/useAppFormUtils";
import SimpleContentModal from "../../common/components/Modals/SimpleContentModal";
import AFNavigation from "./AFNavigation";


interface IAFAFPaymentPage {
    applicationId: string;
}

interface IAFDebitProps {
    data: IPaymentDetails;
    handleBack: (e) => void;
}

interface IContentModalKeys {
    headerKey: string;
    contentKey: string;
}

const AFDirectDebitForm: React.FC<IAFDebitProps> = (props) => {
    const { data, handleBack } = props;

    const history = useHistory();
    const params = useParams<IAFAFPaymentPage>();
    const applicationId = params.applicationId;
    const [firstname, setFirstname] = useState("");
    const [questionReady, setQuestionReady] = useState(false);
    const [formReadyToSubmit, setFormReadyToSubmit] = useState(false);
    const [questionList, setQuestionList] = useState<any>([]);
    const [currentQuestion, setcurrentQuestion] = useState<any>();
    const [formAnswerList, setFormAnswerList] = useState<IAnswerProps[]>([]);
    const [daysList, setDaysList] = useState<any[]>();
    const [selectedDay, setSelectedDay] = useState("");
    const [signature, setSignature] = useState("");
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [debitDetails, setDebitDetails] = useState<IDebitDetails>();
    const [modalHeader, setModalHeader] = useState<string>();
    const [modalContent, setModalContent] = useState<string>();
    const [isOpenContent, setIsOpenContent] = useState(false);
    const [hasPaperlessAuth, setHasPaperlessAuth] = useState<string>();

    const bottomRef = useRef(null);

    const { selectors, actions } = useContext(AppFormControllerContext);
    const { checkFormList, createAnswerList } = useAppFormUtils();

    const getQuestion = (questions): void => {
        if (!questions?.length) return;
        setQuestionList(questions);
        setcurrentQuestion(questions[questions?.length - 1]);
        if (elementRef.current) {
            elementRef.current.focus();
        }
    };

    const handleDebitDetails = (): void => {
        const details = {
            ...debitDetails,
            preferredDay: parseInt(selectedDay),
            agreedToTerms: agreeTerms,
            signature: signature,
            applicationId: selectors.getApplicationId(),
            appDirectDebitDetailsId: data?.debitDetails?.appDirectDebitDetailsId || 0
        };
        setDebitDetails(details);
        const params = {
            paymentMethod: "DirectDebit",
            debitDetails: details
        };
        addPayment(selectors.getApplicationId(), selectors.getQuoteGroupId(), params)
            .then(() => {
                const pageInfo: IPageInfo = {
                    pageType: PageType.Consent,
                    currentClientNumber: 1
                };
                setCurrentPageProgress(
                    selectors.getApplicationId(),
                    selectors.getQuoteGroupId(),
                    pageInfo
                );
                history.push(`/app-form/submit/${applicationId}/consent/disclosure`);
            })
            .catch(() => useErrorLogger());
    };

    const handleAnswerDetails = (answerList): void => {
        const details = answerList.reduce((acc, item) => {
            const value = selectors.getEachFormData(item.questionId);
            let info = {};
            switch (item?.questionId) {
                case 1:
                    info = {
                        accountName: item?.answer || value?.answer || ""
                    };
                    break;
                case 2:
                    info = item?.answer ? JSON.parse(item?.answer) : {};
                    break;
                case 3:
                    info = { paymentFrequency: parseInt(item?.answer) };
                    break;
                default:
            }
            acc = {
                ...acc,
                ...info
            };
            return acc;
        }, {});
        setDebitDetails(details);
    };

    const handleFormReady = (answerList) => {
        const status = checkFormList(questionList, answerList, selectors);
        setFormReadyToSubmit(status);
    };

    const displayContentModal = (headerKey: string, contentKey: string): void => {
        var sectionContent = selectors.getSectionContent();
        if (!sectionContent) return;

        var header = sectionContent[headerKey];
        var content = sectionContent[contentKey];

        if (!header || !content) return;

        setModalHeader(header);
        setModalContent(content);
        setIsOpenContent(true);
    };


    const handleAnswers = (answerParam: IAnswerProps): void => {
        actions.setFormData({
            id: answerParam?.questionId,
            data: answerParam
        });
        const answerList = createAnswerList(questionList, formAnswerList, answerParam);
        setFormAnswerList(answerList);
        handleAnswerDetails(answerList);
        handleFormReady(answerList);
    };

    const handleSubmit = (): void => {
        if (!formReadyToSubmit) return;
        const currentListCount = questionList?.length;
        if (DirectDebitQuestionList?.length === currentListCount) {
            handleNextQuestion();
            return;
        }
        let newList = questionList;
        const currentQuestion = DirectDebitQuestionList[currentListCount];
        setcurrentQuestion(currentQuestion);
        newList.push(currentQuestion);
        setQuestionList(newList);
        setFormReadyToSubmit(false);
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleNextQuestion = (): void => {
        handleDebitDetails();
    };

    const isNextButtonDisabled = (): boolean => {
        return currentQuestion?.id === 3
            ? !formReadyToSubmit || !signature || !agreeTerms || !daysList
            : !formReadyToSubmit;
    };

    const handleGetDefaultAnswer = (id): any => {
        const {
            accountName,
            paymentFrequency,
            accountBank,
            accountNumber,
            accountBranch,
            accountSuffix
        } = data?.debitDetails;

        switch (id) {
            case 1:
                return accountName;
            case 2:
                return JSON.stringify({
                    accountBank,
                    accountNumber,
                    accountBranch,
                    accountSuffix
                });
            case 3:
                return paymentFrequency;
            default:
                return "";
        }
    };

    const elementRef = useRef(null);
    const handleElementRef = (el) => {
        elementRef.current = el;
    };

    useEffect(() => {
        var existingContent = selectors.getSectionContent();
        if (existingContent) {
            setHasPaperlessAuth(existingContent["ddPaperlessContent"]);
            return;
        }
        const applicationId = selectors.getApplicationId();
        const quoteGroupId = selectors.getQuoteGroupId();
        getSectionContent(applicationId, quoteGroupId).then((res) => {
            actions.setSectionContent(res || {});
            setHasPaperlessAuth(res && res["ddPaperlessContent"]);
        });
    }, []); // Only run once on startup

    useEffect(() => {
        actions.resetFormData();
        const info =
            selectors.getApplicantList()?.length && selectors.getApplicantList()[0];
        setFirstname(info?.firstName);
        let questions = DirectDebitQuestionList;
        setDaysList([{ label: "Select", value: "" }, ...getOptions(1, 31)]);
        if (Boolean(data?.debitDetails)) {
            questions = DirectDebitQuestionList.map((item) => {
                const answer = handleGetDefaultAnswer(item.id);

                return {
                    ...item,
                    answer
                };
            });
            setSelectedDay(String(data?.debitDetails?.preferredDay));
            setAgreeTerms(data?.debitDetails?.agreedToTerms);
            setSignature(data?.debitDetails?.signature);
        }
        if (!applicationId) return;
        getQuestion(Boolean(data?.debitDetails) ? questions : [questions[0]]);
    }, [data]);


    return (
        <>
            <SimpleContentModal
                isOpen={isOpenContent}
                onClose={() => {
                    setIsOpenContent(false);
                }}
                header={modalHeader}
                content={modalContent}
            />
            <Formik initialValues={{}} onSubmit={handleSubmit}>
                {() => (
                    <Form autoComplete="off" className={cx(styles["direct-debit"])}>
                        {questionList?.map((question, idx) => (
                            <div
                                className={cx(styles["question-form"])}
                                key={idx}
                                id={`${question.id}-${idx}`}>
                                {question?.questionText && (
                                    <label
                                        className={cx(styles["question-form-label"])}
                                        htmlFor={question?.questionText}
                                        dangerouslySetInnerHTML={{
                                            __html: question?.questionText || ""
                                        }}></label>
                                )}

                                <div className={styles["question-form-field"]}>
                                    <AppFormField
                                        question={question}
                                        answerChanges={handleAnswers}
                                        fieldReady={setQuestionReady}
                                        labelText={question?.questionText}
                                    />
                                </div>
                            </div>
                        ))}
                        {currentQuestion?.id === 3 && (
                            <div className={styles["af-bank-signature"]}>
                                <label
                                    className={styles["af-bank-signature--label"]}
                                    htmlFor="Day">
                                    Day
                                </label>
                                <div
                                    className={cx(
                                        styles["af-dropdown"],
                                        styles["af-bank-dropdown-wrapper"]
                                    )}>
                                    <Dropdown
                                        id="Day"
                                        name="Day"
                                        options={daysList}
                                        selectedOptionId={selectedDay}
                                        onChange={(e) => {
                                            setSelectedDay(e.target.value);
                                            handleFormReady(formAnswerList);
                                        }}
                                        variant={"default"}
                                        className={styles["af-dropdown--style"]}
                                    />
                                    <span className={styles["af-bank-dropdown--label"]}>
                                        Which day of each month would you prefer your
                                        payments to be deducted?
                                    </span>
                                </div>
                                <Checkbox
                                    id="agree-terms"
                                    name="agree-terms"
                                    checked={agreeTerms}
                                    onChange={(e) => {
                                        setAgreeTerms(e.target.checked);
                                        handleFormReady(formAnswerList);
                                    }}
                                    onBlur={() => {}}
                                    size="m"
                                    className={cx("gap-ml", styles["af-terms-check"])}
                                    label={
                                        <span
                                            className={cx(
                                                "space-pre-m",
                                                styles["af-terms-check--label"]
                                            )}>
                                            I have read and understand the{" "}
                                            <a href="#" onClick={() => displayContentModal("ddTermsHeader", "ddTermsContent" )}>
                                                Direct Debit Terms and Conditions
                                            </a>
                                            {hasPaperlessAuth && (<>
                                                {" "}and{" "}
                                                <a href="#" onClick={() => displayContentModal("ddPaperlessHeader", "ddPaperlessContent")}>
                                                    Paperless Authorisations
                                                </a>
                                            </>)}
                                            .
                                        </span>
                                    }
                                />
                                <label
                                    className={styles["af-bank-signature--label"]}
                                    htmlFor="Signature">
                                    Signature ({firstname})
                                </label>
                                <Input
                                    id="Signature"
                                    name="Signature"
                                    type="text"
                                    value={signature}
                                    onChange={(e) => {
                                        setSignature(e.target.value);
                                        handleFormReady(formAnswerList);
                                    }}
                                    textRef={handleElementRef}
                                />
                                <span className={styles["af-bank-signature--span"]}>
                                    To sign please type your full name
                                </span>
                            </div>
                        )}
                        <AFNavigation
                            handleBack={handleBack}
                            forwardIsVisible={true}
                            skipIsVisible={false}
                            forwardIsDisabled={isNextButtonDisabled()}
                            forwardType="submit"
                            forwardText={"Next"}
                        />
                        <div ref={bottomRef} />
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default AFDirectDebitForm;
